import { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../../api/apiClient";
import { DEFAULT_COLUMNS, ALL_COLUMN_KEYS, isLocationColumn } from "../constants/columnRegistry";

const STORAGE_KEY = "dashboard_columns";
const API_KEY = "dashboard.columns";

const PINNED_HEAD = ["color", "product_name"];
const PINNED_TAIL = ["actions"];
const PINNED_SET = new Set([...PINNED_HEAD, ...PINNED_TAIL]);

function enforceColumnOrder(cols) {
    const middle = cols.filter((k) => !PINNED_SET.has(k));
    const head = PINNED_HEAD.filter((k) => cols.includes(k));
    const tail = PINNED_TAIL.filter((k) => cols.includes(k));
    return [...head, ...middle, ...tail];
}

function loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every((k) => ALL_COLUMN_KEYS.includes(k) || isLocationColumn(k))) {
            return enforceColumnOrder(parsed);
        }
    } catch { /* corrupted */ }
    return null;
}

function saveToStorage(columns) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
}

export default function useColumnPreferences() {
    const [columns, setColumnsState] = useState(() => loadFromStorage() || DEFAULT_COLUMNS);
    const [isLoading, setIsLoading] = useState(true);
    const syncedRef = useRef(false);

    useEffect(() => {
        if (syncedRef.current) return;
        syncedRef.current = true;

        apiClient.get(`/preferences/${API_KEY}`)
            .then((res) => {
                const serverCols = res.data?.value;
                if (Array.isArray(serverCols) && serverCols.length > 0) {
                    const valid = enforceColumnOrder(serverCols.filter((k) => ALL_COLUMN_KEYS.includes(k) || isLocationColumn(k)));
                    if (valid.length > 0) {
                        setColumnsState(valid);
                        saveToStorage(valid);
                    }
                }
            })
            .catch(() => { /* use localStorage fallback silently */ })
            .finally(() => setIsLoading(false));
    }, []);

    const setColumns = useCallback((newColumns) => {
        const safe = enforceColumnOrder(newColumns);
        setColumnsState(safe);
        saveToStorage(safe);
        apiClient.put(`/preferences/${API_KEY}`, { value: safe }).catch(() => {});
    }, []);

    const resetToDefaults = useCallback(() => {
        setColumns(DEFAULT_COLUMNS);
    }, [setColumns]);

    return { columns, setColumns, resetToDefaults, isLoading };
}
